import {ListReportsResponse} from "src/models/reports/ListReportResponse";
import {useEffect, useState} from "react";
import apiService from "src/services/ApiCallService";
import ErrorUtils from "src/utils/ErrorUtils";
import {DownloadReportRequest, DownloadReportResponse} from "src/models/reports/DownloadReport";
import {GetReportingPeriodsResponse} from "src/models/reports/GetReportingPeriodListResult";
import {GenerateReportRequest, GenerateReportResponse} from "src/models/reports/GenerateReport";
import {TaxAuditRecordsRequest, TaxAuditRecordsResponse} from "src/models/reports/TaxAuditRecords";
import {TPTaxVarianceRecord, TPTaxVarianceRecordsRequest, TPTaxVarianceRecordsResponse} from "src/models/reports/TPTaxVarianceRecords";
import { TPReportsSummaryRequest,TPReportsSummaryResponse} from "src/models/reports/TPReportsSummary";
import { ReportGenerationSummaryRequest, ReportGenerationSummaryResponse } from "src/models/reports/ReportGenerationSummary";
import {TaxAuditRecordDetailsResponse} from "src/models/reports/TaxAuditRecordDetails";
import {DownloadTaxAuditRecordRequest, DownloadTaxAuditRecordResponse} from "src/models/reports/DownloadTaxAuditRecord";
import {
    GetCLIsByProviderCompanyCodeRequest,
    GetCLIsResponse
} from "src/models/reports/TPTaxReport";
import {FetchReportRequest, FetchReportResponse} from "src/models/reports/FetchReport";
import {
    DownloadIndirectTaxChangeRecordsRequest,
    DownloadIndirectTaxChangeRecordsResponse
} from "src/models/reports/DownloadIndirectTaxChangeRecords";
import {
    FetchIndirectTaxChangeRecordsRequest,
    FetchIndirectTaxChangeRecordsResponse
} from "src/models/reports/FetchIndirectTaxChangeRecords";
import {
    GetCLIExecutionRecordsRequest,
    GetCLIExecutionRecordsResponse
} from "src/models/reports/GetCLIExecutionRecords";
import { COADataRecord } from "src/models/reports/GetCOAData";
import {GetTPReportFiltersRequest, GetTPReportFiltersResponse} from "src/models/reports/GetTPReportFilters";
import {
    GetFunctionalSegmentCategoriesByEntityRequest,
    GetFunctionalSegmentCategoriesByEntityResponse
} from "src/models/reports/GetFunctionalSegmentCategoriesByEntity";
import {DownloadTPTaxMultiCLIReportRequest, DownloadTPTaxMultiCLIReportResponse} from "src/models/reports/DownloadTPTaxMultiCLIReport";
import {GetAccountMappingActivityLogsResponse, GetAccountMappingResponse} from "src/models/reports/GetAccountMapping";
import {
    GetFiscalYearActiveCLIsForWorkbookRequest,
    GetFiscalYearActiveCLIsForWorkbookResponse
} from "src/models/reports/GetFiscalYearActiveCLIsForWorkbook";
import {UpdateAccountMappingRequest, UpdateAccountMappingResponse} from "src/models/reports/UpdateAccountMapping";
import {GetTPReportVersionsResponse, isVersionFailed, ReportVersionsResult, toReportVersion} from "src/models/reports/ReportVersions";
import {TriggerOnDemandFullRefreshResponse} from "src/models/reports/TriggerOnDemandFullRefresh";
import {isQuarterEndReportingPeriod, toOrchestratorPeriod} from "src/models/reports/ReportPeriod";

export default class ReportsService {

    /**
     * Lists the report-generation runs (versions) for a period from the ICTPReportingOrchestrator
     * GetTPReportVersions op. Returns the enriched versions plus the period's quarter-end-close flag.
     * On error (e.g. the orchestrator is unreachable) it resolves to an empty version list so the
     * redesigned section degrades gracefully to its "no reports for this period" state.
     */
    getReportVersions(period: string, refreshKey?: number): [ReportVersionsResult, boolean, string] {
        const [result, setResult] = useState<ReportVersionsResult>({ versions: [], isQuarterEndPeriod: false });
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            // Guards against a stale in-flight fetch clobbering current state: on a rapid period/refresh
            // change the previous effect's cleanup sets cancelled=true, so its late-resolving response is
            // ignored rather than overwriting the new period's data (and its stale isQuarterEndPeriod).
            let cancelled = false;
            const isQuarterEndPeriod = isQuarterEndReportingPeriod(period);
            // Clear the previous period's versions immediately so the selector/cards don't show stale
            // entries from the old period while the new period's fetch is in flight.
            setResult({ versions: [], isQuarterEndPeriod });
            async function fetchData() {
                try {
                    setLoading(true);
                    setError('');
                    // The orchestrator expects the period as yyyyMM (e.g. "202604"); the selector uses
                    // the MMM-YY display format (e.g. "APR-26"), so convert at the API boundary.
                    const response = await apiService.getTPReportVersions(toOrchestratorPeriod(period));
                    const json = await response.json() as GetTPReportVersionsResponse;
                    if (cancelled) {
                        return;
                    }
                    setResult({
                        // The orchestrator already collapses to at most one entry per kind (latest Day 7 /
                        // Day 11 / On-Demand), ordered Day 7 -> Day 11 -> On-Demand. Hide trigger-failed
                        // runs entirely (nothing to view); running runs are kept but shown as non-selectable
                        // in the selector, and COMPLETE / PARTIAL runs are selectable.
                        versions: (json.versions ?? [])
                            .map(toReportVersion)
                            .filter((v) => !isVersionFailed(v.status)),
                        isQuarterEndPeriod,
                    });
                } catch (ex) {
                    if (cancelled) {
                        return;
                    }
                    setError(ErrorUtils.getMessage(ex));
                    // Degrade to an empty list (keeping the derived QEC flag) rather than leaving stale data.
                    setResult({ versions: [], isQuarterEndPeriod });
                } finally {
                    if (!cancelled) {
                        setLoading(false);
                    }
                }
            }
            if (period) {
                fetchData();
            } else {
                setResult({ versions: [], isQuarterEndPeriod });
            }
            return () => {
                cancelled = true;
            };
        }, [period, refreshKey]);
        return [result, loading, error];
    }

    /**
     * Triggers an on-demand full TP Tax report refresh for a period via the ICTPReportingOrchestrator
     * TriggerOnDemandFullRefresh op. Returns the settled result: the accepted job's response, a loading
     * flag, and an error message. The caller drives it by passing a non-null period (yyyyMM) and re-arms
     * it (period -> null) after the attempt settles so a repeat trigger with the same period re-fires.
     * @param period The reporting period to refresh, in yyyyMM format (null while idle).
     */
    triggerOnDemandFullRefresh(period: string | null): [TriggerOnDemandFullRefreshResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as TriggerOnDemandFullRefreshResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            // See getReportVersions: guard a late-resolving trigger response from clobbering state after
            // the period changed / the hook re-armed.
            let cancelled = false;
            async function fetchData(refreshPeriod: string) {
                // Clear prior-attempt state so a retry after a failure does not leave a stale error (or
                // stale success) that would show alongside the new attempt's result banner.
                setError('');
                setResult(null as unknown as TriggerOnDemandFullRefreshResponse);
                try {
                    setLoading(true);
                    const response = await apiService.triggerOnDemandFullRefresh(refreshPeriod);
                    const json = await response.json() as TriggerOnDemandFullRefreshResponse;
                    if (cancelled) {
                        return;
                    }
                    setResult(json);
                } catch (ex) {
                    if (cancelled) {
                        return;
                    }
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    if (!cancelled) {
                        setLoading(false);
                    }
                }
            }
            if (period) {
                fetchData(period);
            }
            return () => {
                cancelled = true;
            };
        }, [period]);
        return [result, loading, error];
    }

    fetchReportHistory(username?: string, refresh?:boolean): [ListReportsResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as ListReportsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.listReports(username);
                    const json = await response.json() as ListReportsResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (refresh){
                fetchData();
            }
        }, [refresh]);
        return [results, loading, error];
    }

    downloadReport(payload?: DownloadReportRequest): [string, boolean, string] {
        const [result, setResult] = useState('');
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    setError('');
                    const response = await apiService.downloadReport(payload);
                    const json = await response.json() as DownloadReportResponse;
                    setResult(json.reportURL)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [result, loading, error];
    }

    // skip: when true, the reporting-period list is NOT fetched. Used by the report pages under the
    // event-driven redesign, where period + accounting day come from the dashboard (URL query) and the
    // on-page period dropdown is hidden — so the periods call is unnecessary. Defaults to false so every
    // existing caller keeps fetching unconditionally.
    getReportingPeriods(skip = false): [GetReportingPeriodsResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as GetReportingPeriodsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            if (skip) {
                return;
            }
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getReportingPeriods();
                    const json = await response.json() as GetReportingPeriodsResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            fetchData();
        }, [skip]);
        return [results, loading, error];
    }

    generateReport(payload?: GenerateReportRequest | null): [GenerateReportResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as GenerateReportResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.generateReport(payload);
                    const json = await response.json() as GenerateReportResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload){
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    searchTaxAuditRecords(payload?: TaxAuditRecordsRequest): [TaxAuditRecordsResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as TaxAuditRecordsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.searchTaxAuditRecords(payload);
                    const json = await response.json() as TaxAuditRecordsResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    getCLIExecutionRecords(payload?: GetCLIExecutionRecordsRequest): [GetCLIExecutionRecordsResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as GetCLIExecutionRecordsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getCLIExecutionRecords(payload);
                    const json = await response.json() as any;

                    const transformedResponse = {
                        ...json,
                        cliExecutionRecordsList: json.cliExecutionRecordsList?.map((cli: any) => ({
                            ...cli,
                            subRows: cli.jeExecutionRecordsList?.filter(Boolean)
                        }))
                    };
                    
                    setResults(transformedResponse as GetCLIExecutionRecordsResponse);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    getTaxAuditRecordDetails(taxJournalHeaderId: string): [TaxAuditRecordDetailsResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as TaxAuditRecordDetailsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getTaxAuditRecordDetails(taxJournalHeaderId);
                    const json = await response.json() as TaxAuditRecordDetailsResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!taxJournalHeaderId) {
                fetchData();
            }
        }, [taxJournalHeaderId]);
        return [results, loading, error];
    }

    downloadTaxAuditRecord(payload?: DownloadTaxAuditRecordRequest): [string, boolean, string] {
        const [result, setResult] = useState('');
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.downloadTaxAuditRecord(payload);
                    const json = await response.json() as DownloadTaxAuditRecordResponse;
                    setResult(json.reportURL);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [result, loading, error];
    }

    getCLIsByProviderCompanyCode(payload: GetCLIsByProviderCompanyCodeRequest | null) : [GetCLIsResponse, boolean, string] {
        const [result, setResult] = useState(null as unknown as GetCLIsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async  function fetchData(providerCompanyCode: string, calculationStatus: string) {
                try{
                    setLoading(true);
                    const response = await  apiService.getCLIsByProviderCompanyCode(providerCompanyCode,
                        calculationStatus)
                    const json = await response.json() as GetCLIsResponse;
                    setResult(json)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (!!payload) {
                fetchData(payload.providerCompanyCode, payload.calculationStatus);
            }
        }, [payload]);
        return [result, loading, error];
    }

    fetchReportData(payload: FetchReportRequest | null) : [FetchReportResponse, boolean, string]{
        const [result, setResult] = useState(null as unknown as FetchReportResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async  function fetchData() {
                try{
                    setLoading(true);
                    const response = await  apiService.fetchReportData(payload)
                    const json = await response.json() as FetchReportResponse;
                    setResult(json)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [result, loading, error];
    }

    fetchIndirectTaxChangeRecords(payload?: FetchIndirectTaxChangeRecordsRequest): [FetchIndirectTaxChangeRecordsResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as FetchIndirectTaxChangeRecordsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');


        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.fetchIndirectTaxChangeRecords(payload);
                    const json = await response.json() as FetchIndirectTaxChangeRecordsResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    fetchCompleteTPTaxVarianceRecords(payload?: TPTaxVarianceRecordsRequest): [TPTaxVarianceRecord[], boolean, string] {
        const [results, setResults] = useState<TPTaxVarianceRecord[]>([]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    let allRecords: TPTaxVarianceRecord[] = [];
                    let lastEvaluatedKey: string | null = null;
                    do {
                        const response = await apiService.fetchTPTaxVarianceRecords(payload);
                        const json = await response.json() as TPTaxVarianceRecordsResponse;
                        lastEvaluatedKey = json.lastEvaluatedKey
                        if(lastEvaluatedKey!=null && payload != undefined){
                            payload.startKey = lastEvaluatedKey;
                        }
                        allRecords = [...allRecords, ...json.records];
                    } while(lastEvaluatedKey!=null)
                    
                    setResults(allRecords);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    fetchCompleteTPReportsSummary(payload?: TPReportsSummaryRequest): [TPReportsSummaryResponse, boolean, string]{
     const [loading, setLoading] = useState(false);
     const [error, setError] = useState('');
     const [results, setResults] = useState(null as unknown as TPReportsSummaryResponse);

     useEffect(() => {
             async function fetchData() {
                 try {
                     setLoading(true);
                     const response = await apiService.fetchTPReportsSummary(payload);
                     const json = await response.json() as TPReportsSummaryResponse;
                     setResults(json)
                 } catch (ex) {
                     setError(ErrorUtils.getMessage(ex));
                 } finally {
                     setLoading(false);
                 }
             }
             if (!!payload) {
                 fetchData();
             }
         }, [payload]);
     return [results,loading,error];
    }

    // TODO: Remove mock data once GetReportGenerationSummary API is deployed (Phase 4)
    fetchReportGenerationSummary(payload?: ReportGenerationSummaryRequest): [ReportGenerationSummaryResponse, boolean, string] {
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [results, setResults] = useState(null as unknown as ReportGenerationSummaryResponse);

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    setError('');
                    setResults(null as unknown as ReportGenerationSummaryResponse);
                    const response = await apiService.fetchReportGenerationSummary(payload);
                    const json = await response.json() as ReportGenerationSummaryResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    downloadIndirectTaxChangeRecords(payload: DownloadIndirectTaxChangeRecordsRequest): [string, boolean, string] {
        const [result, setResult] = useState('');
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.downloadIndirectTaxChangeRecords(payload.reportId);
                    const json = await response.json() as DownloadIndirectTaxChangeRecordsResponse;
                    setResult(json.reportURL)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload && !!payload.reportId) {
                fetchData();
            }
        }, [payload]);
        return [result, loading, error];
    }

    getCOADataBySegment(segment:string): [COADataRecord[], boolean, string] {
        const [result, setResult] = useState([] as COADataRecord[]);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');


        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getCOADataBySegment(segment);
                    const json = await response.json();
                    setResult(json.coaDataList);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if(segment!= ''){
                fetchData()
            }
        }, [segment]);

        return [result, loading, error];
    }

    getTPReportFilters(payload: GetTPReportFiltersRequest | null): [GetTPReportFiltersResponse, boolean, string] {
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [results, setResults] = useState(null as unknown as GetTPReportFiltersResponse);

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getTPReportFilters(payload);
                    const json = await response.json() as GetTPReportFiltersResponse;
                    setResults(json)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results,loading,error];
    }

    getFunctionalSegmentCategoriesByEntity(payload: GetFunctionalSegmentCategoriesByEntityRequest | null): [GetFunctionalSegmentCategoriesByEntityResponse, boolean, string] {
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [results, setResults] = useState(null as unknown as GetFunctionalSegmentCategoriesByEntityResponse);

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getFunctionalSegmentCategoriesByEntity(payload);
                    const json = await response.json() as GetFunctionalSegmentCategoriesByEntityResponse;
                    setResults(json)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results,loading,error];
    }

    downloadTPTaxMultiCLIReport(payload: DownloadTPTaxMultiCLIReportRequest | null): [DownloadTPTaxMultiCLIReportResponse, boolean, string] {
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [results, setResults] = useState(null as unknown as DownloadTPTaxMultiCLIReportResponse);

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.downloadTPTaxMultiCLIReport(payload);
                    const json = await response.json() as DownloadTPTaxMultiCLIReportResponse;
                    setResults(json)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }
            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results,loading,error];
    }

    getAccountMapping(mappingType: string, exceptionMappingFor?: string, refresh?: boolean): [GetAccountMappingResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as GetAccountMappingResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getAccountMapping(mappingType, exceptionMappingFor);
                    const json = await response.json() as GetAccountMappingResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (mappingType) {
                fetchData();
            }
        }, [mappingType, exceptionMappingFor, refresh]);
        return [results, loading, error];
    }

    getFiscalYearActiveCLIsForWorkbook(payload: GetFiscalYearActiveCLIsForWorkbookRequest | null): [GetFiscalYearActiveCLIsForWorkbookResponse, boolean, string] {
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const [results, setResults] = useState(null as unknown as GetFiscalYearActiveCLIsForWorkbookResponse);

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getFiscalYearActiveCLIsForWorkbook(payload);
                    const json = await response.json() as GetFiscalYearActiveCLIsForWorkbookResponse;
                    setResults(json)
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    updateAccountMapping(payload: UpdateAccountMappingRequest|null): [UpdateAccountMappingResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as UpdateAccountMappingResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.updateAccountMapping(payload);
                    const json = await response.json() as UpdateAccountMappingResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            if (!!payload) {
                fetchData();
            }
        }, [payload]);
        return [results, loading, error];
    }

    getAccountMappingActivityLogs(refresh?: boolean): [GetAccountMappingActivityLogsResponse, boolean, string] {
        const [results, setResults] = useState(null as unknown as GetAccountMappingActivityLogsResponse);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        useEffect(() => {
            async function fetchData() {
                try {
                    setLoading(true);
                    const response = await apiService.getAccountMappingActivityLogs();
                    const json = await response.json() as GetAccountMappingActivityLogsResponse;
                    setResults(json);
                } catch (ex) {
                    setError(ErrorUtils.getMessage(ex));
                } finally {
                    setLoading(false);
                }
            }

            fetchData();
        }, [refresh]);
        return [results, loading, error];
    }
}

